import React from 'react';

import { Badge, createStyles, Group, Loader, Stack, Table, Text, Title } from '@mantine/core';
import { IconTrophy } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import { WorkoutDetailView } from 'components/Workout';
import useWorkout from 'hooks/workout/useWorkout';
import useWorkoutAthleteResults from 'hooks/workoutAthleteResult/useWorkoutAthleteResults';
import { WorkoutHistoryEntry } from 'types';
import { WORKOUT_LEVEL_LABEL_MAP, WorkoutLevelEnum } from 'types/enums';
import { formatDateForDisplay } from 'utils/date-functions';

/** Trailing zeros off a decimal string, without going through a float. */
const trimTrailingZeros = (value: string): string =>
  value.includes('.') ? value.replace(/\.?0+$/, '') : value;

const useStyles = createStyles(theme => ({
  scoring: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    padding: theme.spacing.md,
  },
}));

interface WorkoutSidePanelProps {
  athleteId: string;
  movementName: string;
  entry: WorkoutHistoryEntry | null;
  opened: boolean;
  close: () => void;
}

/**
 * The workout an entry was logged in, opened beside the history rather than navigated to.
 *
 * WorkoutDetailView renders the prescription; the athlete's result goes into the movement
 * it was logged against, located by the section movement id the entry carries. If that
 * does not resolve, the result falls back to a card above the workout.
 *
 * Only that one movement is scored — the others carry no formatted result to render.
 */
const WorkoutSidePanel = ({
  athleteId,
  movementName,
  entry,
  opened,
  close,
}: WorkoutSidePanelProps): JSX.Element => {
  const { classes } = useStyles();
  const workoutId = entry?.workoutId ?? undefined;

  const { data: workoutData, isLoading: workoutLoading, isError: workoutError } = useWorkout(workoutId);
  // Scoped to one athlete, so the rest of the class never reaches the browser.
  const {
    data: resultsData,
    isLoading: resultsLoading,
    isError: resultsError,
  } = useWorkoutAthleteResults(workoutId, athleteId, { poll: false });

  const workout = workoutData?.data;
  const loggedSets =
    resultsData?.data
      ?.flatMap(athleteResult => athleteResult.results)
      .find(result => result.workoutResultId === entry?.workoutResultId)?.sets ?? [];

  // Matched on the section movement id, so a workout programming the same movement twice
  // resolves to the row that was scored — and the unit comes off that row.
  const placement = workout?.sections
    ?.flatMap(section => section.movements.map(movement => ({ section, movement })))
    .find(({ movement }) => movement.workoutSectionMovementId === entry?.workoutSectionMovementId);
  const loggedUnit = placement?.movement.weightUnit;

  const title = workout ? `${workout.name} — ${formatDateForDisplay(workout.date)}` : 'Workout';

  const resultSlot = entry && (
    <Stack spacing={4} sx={{ minWidth: 160, flexShrink: 0 }}>
      <Text size='xs' weight={600} color='dimmed' tt='uppercase' align='right'>
        Athlete&apos;s result
      </Text>
      <Group spacing='xs' position='right' noWrap>
        <Text size='sm' weight={600}>
          {entry.result}
        </Text>
        {entry.isPr && <IconTrophy size={14} color='#f59f00' aria-label='Personal record' />}
        {entry.level && (
          <Badge variant='outline' size='xs' color='gray'>
            {WORKOUT_LEVEL_LABEL_MAP[entry.level as WorkoutLevelEnum]}
          </Badge>
        )}
      </Group>

      {/* Only the breakdown fails here, never the result: that string comes from the entry
          the panel was opened with. Said out loud rather than left as an empty table, which
          reads as an athlete who logged no sets. */}
      {resultsError && (
        <Text size='xs' color='dimmed' align='right'>
          The set breakdown could not be loaded.
        </Text>
      )}

      {!resultsError && loggedSets.length > 0 && (
        <>
          {/* The result above reads in the gym's unit; these are the numbers the athlete
              typed, in the unit the coach set on this movement. Labelled so a converted
              total and its sets are not read as disagreeing. */}
          <Text size='xs' color='dimmed' tt='uppercase' align='right' mt={4}>
            As logged
          </Text>
          <Table fontSize='sm'>
            <tbody>
              {loggedSets.map(loggedSet => (
                <tr key={loggedSet.setIndex}>
                  <td>
                    <Text size='sm' color='dimmed'>
                      Set {loggedSet.setIndex + 1}
                    </Text>
                  </td>
                  <td align='right'>
                    {loggedSet.repsCompleted} {loggedSet.repsCompleted === 1 ? 'rep' : 'reps'} @{' '}
                    {trimTrailingZeros(loggedSet.weightValue)}
                    {loggedUnit ? ` ${loggedUnit}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Stack>
  );

  return (
    <Drawer
      opened={opened}
      onClose={close}
      position='right'
      size='xl'
      title={<Title order={4}>{title}</Title>}
    >
      {workoutError ? (
        <Text size='sm' color='dimmed' py='xl' align='center'>
          This workout could not be loaded. Close the panel and try again.
        </Text>
      ) : workoutLoading || resultsLoading || !workout || !entry ? (
        <Group position='center' py='xl'>
          <Loader />
        </Group>
      ) : (
        <Stack spacing='lg'>
          {!placement && (
            // Fallback only: the id is non-null and its row lives in this same workout.
            <div className={classes.scoring}>
              <Group position='apart' align='flex-start' noWrap>
                <Text size='sm' weight={600}>
                  {movementName}
                </Text>
                {resultSlot}
              </Group>
            </div>
          )}

          <WorkoutDetailView
            workout={workout}
            isCoachView
            renderMovementExtra={movement =>
              movement.workoutSectionMovementId === entry.workoutSectionMovementId ? resultSlot : null
            }
          />
        </Stack>
      )}
    </Drawer>
  );
};

export default WorkoutSidePanel;
